import { Component, Input } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ThermometerReading, TempDataPoint } from '../version.service';
import { updatedAgo as fmtAgo, tempColor, humidityColor } from '../shared/format.utils';

@Component({
  selector: 'app-klima',
  templateUrl: './klima.component.html',
})
export class KlimaComponent {
  @Input() thermometers:       ThermometerReading[]                    = [];
  @Input() thermometerHistory: {[deviceId: string]: TempDataPoint[]}  = {};
  @Input() thermoFlash         = false;
  @Input() liveDotOn           = true;
  @Input() thermoUpdatedAt:    number | null                           = null;
  @Input() nowTick             = Date.now();
  @Input() darkMode            = false;

  sensorMode: {[deviceId: string]: 'temp' | 'humidity'} = {};

  // Expanded-chart range (Tag = live 24h window, Woche = live 7d, Monat/Jahr = archive).
  range: 'day' | 'week' | 'month' | 'year' = 'day';
  rangeLoading = false;
  private rangePoints: {[deviceId: string]: TempDataPoint[]} = {};
  readonly chartRanges: { key: 'day' | 'week' | 'month' | 'year'; label: string }[] = [
    { key: 'day',   label: 'Tag' },
    { key: 'week',  label: 'Woche' },
    { key: 'month', label: 'Monat' },
    { key: 'year',  label: 'Jahr' },
  ];
  private static readonly RANGE_MS: Record<string, number> = {
    day:   24 * 3600 * 1000,
    week:  7 * 24 * 3600 * 1000,
    month: 30 * 24 * 3600 * 1000,
    year:  365 * 24 * 3600 * 1000,
  };

  constructor(readonly translate: TranslateService) {}

  get rangeLabel(): string {
    return this.chartRanges.find(r => r.key === this.range)?.label ?? '';
  }

  get windowMs(): number {
    return KlimaComponent.RANGE_MS[this.range];
  }

  /** Points for a sensor's chart: live for day/week, fetched archive for month/year. */
  expandedPoints(deviceId: string): TempDataPoint[] {
    if (this.range === 'day' || this.range === 'week') return this.sparklinePoints(deviceId);
    return this.rangePoints[deviceId] ?? [];
  }

  /** Tab-level range: drives every sensor's chart (fetches each device for month/year). */
  setRange(range: 'day' | 'week' | 'month' | 'year'): void {
    this.range = range;
    if (range === 'day' || range === 'week') return;   // live history already covers these
    const to   = Date.now();
    const from = to - KlimaComponent.RANGE_MS[range];
    this.rangeLoading = true;
    const devices = this.thermometers.map(t => t.deviceId);
    let pending = devices.length;
    if (pending === 0) { this.rangeLoading = false; return; }
    for (const deviceId of devices) {
      const series = encodeURIComponent('thermo-' + deviceId);
      fetch(`/api/history?series=${series}&from=${from}&to=${to}&buckets=600`)
        .then(r => r.json())
        .then((pts: any[]) => { this.rangePoints[deviceId] = pts ?? []; })
        .catch(() => {})
        .finally(() => { if (--pending === 0) this.rangeLoading = false; });
    }
  }

  toggleMode(event: Event, deviceId: string): void {
    event.stopPropagation();
    this.sensorMode[deviceId] = this.sensorMode[deviceId] === 'humidity' ? 'temp' : 'humidity';
  }

  getMode(deviceId: string): 'temp' | 'humidity' {
    return this.sensorMode[deviceId] ?? 'temp';
  }

  sensorName(name: string): string {
    const key = `SENSOR_NAMES.${name}`;
    const t = this.translate.instant(key);
    return t === key ? name : t;
  }

  updatedAgo(ts: number | null): string { return fmtAgo(ts, this.nowTick); }

  readonly tempColor = tempColor;
  readonly humidityColor = humidityColor;

  sparklinePoints(deviceId: string): TempDataPoint[] {
    return this.thermometerHistory[deviceId] ?? [];
  }

  sparklineMinMax(deviceId: string): { min: number; max: number; minHum: number; maxHum: number } {
    const pts = this.expandedPoints(deviceId);
    if (!pts?.length) return { min: 0, max: 0, minHum: 0, maxHum: 0 };
    const temps = pts.map(p => p.tempC);
    const hums  = pts.map(p => p.humidity).filter((h): h is number => h != null);
    return {
      min: Math.min(...temps),
      max: Math.max(...temps),
      minHum: hums.length ? Math.min(...hums) : 0,
      maxHum: hums.length ? Math.max(...hums) : 0,
    };
  }
}
